/**
 * Pure helpers for the in-game admin dashboard: number/date formatting,
 * pagination maths and form validation. Kept free of DOM so they can be unit
 * tested (see format.test.ts) and reused by every admin section.
 */

/** 12345 → "12,345". Nullish/NaN render as an em dash, never "NaN". */
export function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

/** Money with two decimals + currency suffix (invoices are EUR). */
export function fmtMoney(n: number | null | undefined, currency = 'EUR'): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(2)} ${currency}`;
}

export function fmtPercent(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${Math.round(n * 100) / 100}%`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${fmtDate(iso)} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Seconds of uptime → "3d 4h 12m" (always at least "0m"). */
export function fmtUptime(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '—';
  const s = Math.floor(sec);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

// ─── Pagination ──────────────────────────────────────────────────────────────

export interface PageInfo {
  page: number;        // 1-based
  pageCount: number;   // at least 1, so the label never reads "Page 1 of 0"
  from: number;        // 1-based index of the first row on this page (0 when empty)
  to: number;          // 1-based index of the last row on this page
  hasPrev: boolean;
  hasNext: boolean;
}

export function pageInfo(total: number, limit: number, offset: number): PageInfo {
  const safeLimit = Math.max(1, Math.floor(limit) || 1);
  const safeTotal = Math.max(0, Math.floor(total) || 0);
  const safeOffset = Math.min(Math.max(0, Math.floor(offset) || 0), Math.max(0, safeTotal - 1));
  const pageCount = Math.max(1, Math.ceil(safeTotal / safeLimit));
  const page = Math.floor(safeOffset / safeLimit) + 1;
  const from = safeTotal === 0 ? 0 : safeOffset + 1;
  const to = Math.min(safeOffset + safeLimit, safeTotal);
  return { page, pageCount, from, to, hasPrev: safeOffset > 0, hasNext: safeOffset + safeLimit < safeTotal };
}

/** "1–20 of 137" (or "No results"). */
export function pageLabel(total: number, limit: number, offset: number): string {
  const p = pageInfo(total, limit, offset);
  if (p.from === 0) return 'No results';
  return `${p.from}–${p.to} of ${fmtNum(total)}`;
}

// ─── Validation ──────────────────────────────────────────────────────────────

export interface PlayerForm {
  nickname: string; email: string; password: string;
  level: string; shrooms: string;
}

/** Returns a field→message map; empty object means the form is valid. */
export function validatePlayerForm(f: PlayerForm): Record<string, string> {
  const errors: Record<string, string> = {};
  const nick = f.nickname.trim();
  if (nick.length < 3) errors.nickname = 'Nickname needs at least 3 characters';
  else if (nick.length > 24) errors.nickname = 'Nickname is at most 24 characters';
  if (!isEmail(f.email)) errors.email = 'Enter a valid email address';
  if (f.password.length < 8) errors.password = 'Password needs at least 8 characters';
  const level = Number(f.level);
  if (f.level !== '' && (!Number.isInteger(level) || level < 1 || level > 999)) errors.level = 'Level must be 1–999';
  const shrooms = Number(f.shrooms);
  if (f.shrooms !== '' && (!Number.isFinite(shrooms) || shrooms < 0)) errors.shrooms = 'Shrooms cannot be negative';
  return errors;
}

export function isEmail(v: string): boolean {
  const s = v.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

/**
 * Give-shrooms dialog: a non-zero integer amount and an optional reason for the
 * ledger line (at most 120 characters — the server's limit). `positiveOnly` is the
 * player card's "Add Shrooms", which only ever adds.
 */
export function validateShroomGrant(
  amount: string, reason: string, opts: { positiveOnly?: boolean } = {},
): Record<string, string> {
  const errors: Record<string, string> = {};
  const n = Number(amount);
  if (amount.trim() === '' || !Number.isFinite(n) || !Number.isInteger(n) || n === 0) {
    errors.amount = opts.positiveOnly ? 'Enter a positive whole number' : 'Enter a non-zero whole number';
  } else if (opts.positiveOnly && n < 0) {
    errors.amount = 'Enter a positive whole number';
  } else if (Math.abs(n) > 1_000_000) {
    errors.amount = 'Amount is capped at 1,000,000';
  }
  if (reason.trim().length > SHROOM_REASON_MAX) errors.reason = `At most ${SHROOM_REASON_MAX} characters`;
  return errors;
}
export const SHROOM_REASON_MAX = 120;

export function validateSettings(s: {
  commissionPercent: number; cryptePerEur: number; minWithdrawalEur: number;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!Number.isFinite(s.commissionPercent) || s.commissionPercent < 0 || s.commissionPercent > 100) {
    errors.commissionPercent = 'Commission must be between 0 and 100';
  }
  if (!Number.isFinite(s.cryptePerEur) || s.cryptePerEur <= 0) {
    errors.cryptePerEur = 'Shrooms per EUR must be greater than 0';
  }
  if (!Number.isFinite(s.minWithdrawalEur) || s.minWithdrawalEur < 0) {
    errors.minWithdrawalEur = 'Minimum withdrawal cannot be negative';
  }
  return errors;
}

/** A filename that is safe on every OS, for downloaded invoices. */
export function invoiceFilename(id: string, invoiceNumber?: string | null): string {
  const base = (invoiceNumber || id || 'download').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return `invoice-${base || 'download'}.pdf`;
}

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in LOCAL time, not an ISO string. */
export function localDateTimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Net = faucets − sinks; used by the economy panel's summary row. */
export function sumAmounts(rows: { amount: number }[] | null | undefined): number {
  if (!rows?.length) return 0;
  return rows.reduce((a, r) => a + (Number.isFinite(r.amount) ? r.amount : 0), 0);
}

/** `YYYY-MM-DD` of a moment in LOCAL time, for an `<input type="date">`. */
export function localDateValue(d: Date): string {
  return localDateTimeValue(d).slice(0, 10);
}

/** `HH:MM` of a moment in LOCAL time, for an `<input type="time">`. */
export function localTimeValue(d: Date): string {
  return localDateTimeValue(d).slice(11, 16);
}

/**
 * A date input + a time input → one ISO instant. The pickers are local time and
 * the API takes an instant. A missing time means midnight; a missing or
 * unreadable date means "no value" (null).
 */
export function combineLocalDateTime(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const t = /^\d{2}:\d{2}(:\d{2})?$/.test(time) ? time : '00:00';
  const at = new Date(`${date}T${t}`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** How recently a player was around, as the player card words it. */
export function activityLabel(a: string | null | undefined): { text: string; tone: 'ok' | 'warn' | 'bad' | 'mute' } {
  switch (a) {
    case 'online': return { text: 'online now', tone: 'ok' };
    case 'today': return { text: 'active today', tone: 'ok' };
    case 'this_week': return { text: 'active this week', tone: 'warn' };
    case 'this_month': return { text: 'active this month', tone: 'warn' };
    case 'dormant': return { text: 'dormant (30+ days)', tone: 'bad' };
    default: return { text: 'never seen', tone: 'mute' };
  }
}

/**
 * "N days / N months / N.N years in the game" from the account's creation; under
 * one day it is "joined today".
 */
export function memberFor(createdAt: string | null | undefined, now: Date = new Date()): string {
  if (!createdAt) return '—';
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((now.getTime() - t) / 86_400_000);
  if (days < 1) return 'joined today';
  if (days < 60) return `${days} day${days === 1 ? '' : 's'} in the game`;
  const months = Math.floor(days / 30.44);
  if (months < 24) return `${months} months in the game`;
  return `${(days / 365.25).toFixed(1)} years in the game`;
}

/**
 * ISO 4217 minor-unit digits. Every one of the 11 invoice currencies has 2; the
 * exceptions are listed so a payment in one of them is never shown 100× off.
 */
const MINOR_DIGITS: Record<string, number> = {
  JPY: 0, KRW: 0, ISK: 0, CLP: 0, VND: 0, XOF: 0, XAF: 0, UGX: 0, PYG: 0,
  BHD: 3, KWD: 3, OMR: 3, JOD: 3, TND: 3, IQD: 3, LYD: 3,
};
export function minorDigits(currency: string): number {
  return MINOR_DIGITS[currency.toUpperCase()] ?? 2;
}

/**
 * One recent-activity amount, signed. Game currencies (SHROOMS / COINS) are whole
 * units; anything else is a fiat payment stored in minor units and shown in major
 * units: 1999 PLN cents → "+19.99 PLN".
 */
export function fmtActivityAmount(delta: number, currency: string): string {
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const abs = Math.abs(delta);
  if (currency === 'SHROOMS') return `${sign}${fmtNum(abs)} Shrooms`;
  if (currency === 'COINS') return `${sign}${fmtNum(abs)} coins`;
  const digits = minorDigits(currency);
  const major = abs / 10 ** digits;
  return `${sign}${major.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${currency.toUpperCase()}`;
}
